import React from "react";
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { appHistory } from "@/router/history";

import ProfileDelegateApi from "api/ProfileDelegateApi";
import { expireSession } from "../../context/AuthContext";
import {
  getApiData,
  getApiErrorCode,
  getApiErrorDetails,
  type ApiError,
  type DelegateUserPayload,
} from "../../types/api";
import type { FlashMessage } from "../../types/state";

export interface PermissionItem {
  slug?: string;
  front_permission?: string;
  permission_name?: string;
  name?: string | { value?: string };
}

export interface DelegateUser {
  id?: number | string;
  last_name?: string;
  first_name?: string;
  email?: string;
  permissions?: PermissionItem[];
}

export interface ProfileDelegateActionsAdapter {
  loadPermissions: () => void;
  unloadPermissions: () => void;
  loadDelegateUsers: () => void;
  unloadDelegateUsers: () => void;
  loadDelegateUser: (id: number | string) => void;
  unloadDelegateUser: () => void;
  createDelegateUser: (formData: DelegateUserPayload) => void;
  updateDelegateUser: (id: number | string, formData: DelegateUserPayload) => void;
  deleteDelegateUser: (id: number | string) => void;
  messageDelegateUser: (message?: FlashMessage | Record<string, never>) => void;
}

export interface ProfileDelegateQueryProps {
  message: FlashMessage | Record<string, never>;
  delegateUsers: DelegateUser[];
  delegateUsersLoaded: boolean;
  permissions: PermissionItem[];
  permissionsLoaded: boolean;
  delegateUser: DelegateUser | null;
  profileDelegateActions: ProfileDelegateActionsAdapter;
}

const EMPTY_PERMISSIONS: PermissionItem[] = [];
const EMPTY_DELEGATE_USERS: DelegateUser[] = [];
const EMPTY_MESSAGE: Record<string, never> = {};

function handleAuthError(error: ApiError): boolean {
  if (getApiErrorCode(error) !== "NOT_LOGGED") {
    return false;
  }

  expireSession();
  return true;
}

function permissionsQueryKey() {
  return ["profileDelegate", "permissions"];
}

function delegateUsersQueryKey() {
  return ["profileDelegate", "delegateUsers"];
}

function delegateUserQueryKey(id?: number | string | null) {
  return ["profileDelegate", "delegateUser", id || "none"];
}

function messageQueryKey() {
  return ["profileDelegate", "message"];
}

function setDelegateMessage(
  queryClient: QueryClient,
  message: FlashMessage | Record<string, never> = EMPTY_MESSAGE
) {
  queryClient.setQueryData(messageQueryKey(), message);
}

function setDelegateError(queryClient: QueryClient, error: ApiError): void {
  if (handleAuthError(error)) {
    return;
  }

  if (
    getApiErrorCode(error) === "CREATE_DELEGATE_USER_ERROR" ||
    getApiErrorCode(error) === "UPDATE_DELEGATE_USER_ERROR"
  ) {
    setDelegateMessage(queryClient, {
      type: "error",
      message: getApiErrorDetails(error),
    } as FlashMessage);
  }
}

export function useDelegatePermissions() {
  const query = useQuery({
    queryKey: permissionsQueryKey(),
    queryFn: () =>
      ProfileDelegateApi.getPermissions()
        .then((res) => getApiData(res, EMPTY_PERMISSIONS) as PermissionItem[])
        .catch((error: ApiError) => {
          handleAuthError(error);
          return EMPTY_PERMISSIONS;
        }),
  });

  return {
    ...query,
    permissions: query.data || EMPTY_PERMISSIONS,
    permissionsLoaded: query.isSuccess,
  };
}

export function useDelegateUsers(enabled = true) {
  const query = useQuery({
    queryKey: delegateUsersQueryKey(),
    enabled,
    queryFn: () =>
      ProfileDelegateApi.getDelegateUsers()
        .then((res) => getApiData(res, EMPTY_DELEGATE_USERS) as DelegateUser[])
        .catch((error: ApiError) => {
          handleAuthError(error);
          return EMPTY_DELEGATE_USERS;
        }),
  });

  return {
    ...query,
    delegateUsers: query.data || EMPTY_DELEGATE_USERS,
    delegateUsersLoaded: query.isSuccess,
  };
}

export function useDelegateUser(id?: number | string | null, enabled = true) {
  const query = useQuery({
    queryKey: delegateUserQueryKey(id),
    enabled: enabled && !!id,
    queryFn: () =>
      ProfileDelegateApi.getDelegateUser(id as number | string)
        .then((res) => getApiData(res, null) as DelegateUser | null)
        .catch((error: ApiError) => {
          handleAuthError(error);
          return null;
        }),
  });

  return {
    ...query,
    delegateUser: query.data || null,
  };
}

export function useProfileDelegateMessage() {
  return useQuery({
    queryKey: messageQueryKey(),
    queryFn: async () => EMPTY_MESSAGE as FlashMessage | Record<string, never>,
    staleTime: Infinity,
    initialData: EMPTY_MESSAGE as FlashMessage | Record<string, never>,
  });
}

export function withProfileDelegateData<P extends ProfileDelegateQueryProps>(
  Component: React.ComponentType<P>,
  options: { delegateUsers?: boolean; delegateUser?: boolean } = {}
): React.ComponentType<Omit<P, keyof ProfileDelegateQueryProps> & { params?: { id?: string } }> {
  return function ProfileDelegateData(props) {
    const shouldLoadDelegateUsers = options.delegateUsers !== false;
    const shouldLoadDelegateUser = options.delegateUser !== false;
    const queryClient = useQueryClient();
    const permissionsQuery = useDelegatePermissions();
    const delegateUsersQuery = useDelegateUsers(shouldLoadDelegateUsers);
    const delegateUserQuery = useDelegateUser(props.params?.id || null, shouldLoadDelegateUser);
    const messageQuery = useProfileDelegateMessage();

    const createDelegateUser = useMutation({
      mutationFn: (formData: DelegateUserPayload) =>
        ProfileDelegateApi.createDelegateUser(formData),
      onSuccess: (res) => {
        const user = getApiData(res, {}) as { id: number | string };
        appHistory.push(BASE_PATH + "/profile/delegate/edit/" + user.id);
        setDelegateMessage(queryClient, {
          type: "success",
          message: "L'utilisateur délégué a bien été créé.",
        });
      },
      onError: (error: ApiError) => setDelegateError(queryClient, error),
    });

    const updateDelegateUser = useMutation({
      mutationFn: ({ id, formData }: { id: number | string; formData: DelegateUserPayload }) =>
        ProfileDelegateApi.updateDelegateUser(id, formData),
      onSuccess: () => {
        appHistory.push(BASE_PATH + "/profile/delegate/list");
        setDelegateMessage(queryClient, {
          type: "success",
          message: "L'utilisateur délégué a bien été modifié.",
        });
      },
      onError: (error: ApiError) => setDelegateError(queryClient, error),
    });

    const deleteDelegateUser = useMutation({
      mutationFn: (id: number | string) => ProfileDelegateApi.deleteDelegateUser(id).then(() => id),
      onSuccess: (id) => {
        queryClient.setQueryData<DelegateUser[]>(delegateUsersQueryKey(), (current = []) =>
          current.filter((user) => user.id !== id)
        );
        setDelegateMessage(queryClient, {
          type: "success",
          message: "L'utilisateur délégué a bien été supprimé.",
        });
      },
      onError: (error: ApiError) => setDelegateError(queryClient, error),
    });

    const profileDelegateActions: ProfileDelegateActionsAdapter = {
      loadPermissions: () => {
        void permissionsQuery.refetch();
      },
      unloadPermissions: () => undefined,
      loadDelegateUsers: () => {
        if (shouldLoadDelegateUsers) {
          void delegateUsersQuery.refetch();
        }
      },
      unloadDelegateUsers: () => undefined,
      loadDelegateUser: (id) => {
        void queryClient.prefetchQuery({
          queryKey: delegateUserQueryKey(id),
          queryFn: () =>
            ProfileDelegateApi.getDelegateUser(id).then(
              (res) => getApiData(res, null) as DelegateUser | null
            ),
        });
      },
      unloadDelegateUser: () => undefined,
      createDelegateUser: (formData) => createDelegateUser.mutate(formData),
      updateDelegateUser: (id, formData) => updateDelegateUser.mutate({ id, formData }),
      deleteDelegateUser: (id) => deleteDelegateUser.mutate(id),
      messageDelegateUser: (message = EMPTY_MESSAGE) => setDelegateMessage(queryClient, message),
    };

    return (
      <Component
        {...(props as P)}
        message={messageQuery.data || EMPTY_MESSAGE}
        delegateUsers={
          shouldLoadDelegateUsers ? delegateUsersQuery.delegateUsers : EMPTY_DELEGATE_USERS
        }
        delegateUsersLoaded={
          shouldLoadDelegateUsers ? delegateUsersQuery.delegateUsersLoaded : false
        }
        permissions={permissionsQuery.permissions}
        permissionsLoaded={permissionsQuery.permissionsLoaded}
        delegateUser={delegateUserQuery.delegateUser}
        profileDelegateActions={profileDelegateActions}
      />
    );
  };
}
